/* eslint-disable @angular-eslint/component-class-suffix */
import { CustomerApplicationProduct, DataEntity, DocumentTypeEnum, FileMapEntry, TableDatumType, VendorApplication } from "common";
import { FullScreenImageDialogComponent } from "../full-screen-image-dialog.component";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from "@angular/material/dialog";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatButtonModule } from "@angular/material/button";
import { Component, Inject, OnInit, inject, signal } from "@angular/core";
import { SharedApiService } from "../../../services/shared-api.service";
import { base64toBlob } from "../../../utils/static-functions";
import { Observable, catchError, firstValueFrom } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ConfirmDialog } from "../confirm.dialog";

@Component({
    selector: 'lib-view-print-email.dialog',
    templateUrl: 'view-print-email.dialog.html',
    standalone: true,
    imports: [MatDialogModule, MatProgressSpinnerModule, MatButtonModule],
})
export class ViewPrintEmailDialog implements OnInit {

    private sharedApiService = inject(SharedApiService);
    private snackbar = inject(MatSnackBar);
    public dialog = inject(MatDialog);
    public disabled = signal(false);
    public showSpinner = signal(false);

    constructor(public dialogRef: MatDialogRef<ViewPrintEmailDialog>,
        @Inject(MAT_DIALOG_DATA) public data: {
            type: TableDatumType,
            customerProduct: CustomerApplicationProduct | undefined,
            vendorApplication: VendorApplication | undefined,
            documentType: DocumentTypeEnum,
            // Fire this action immediately on open instead of waiting for a
            // button click (e.g. the manual-sign "Download Documents" button).
            autoAction?: 'download'
        }
    ) { }

    public ngOnInit(): void {
        // If no data type and no data is provided, close the dialog and alert the user.
        if (this.data.type === undefined || this.data.type === null || (!this.data.customerProduct && !this.data.vendorApplication)) {
            this.dialogRef.close({ action: 'close' });
            this.snackbar.open('Something went wrong. Please try again later.', 'Dismiss');
            return;
        }

        if (this.data.autoAction === 'download') {
            this.onDownload();
        }
    }

    public onView(): void {
        this.disabled.set(true);
        this.showSpinner.set(true);

        this.retrieveBase64ofDocument()
            .pipe(catchError((error) => {
                console.error('Error viewing document:', error);
                this.snackbar.open('Error viewing document. Please try again later.', 'Close', { duration: 5000 });
                this.dialogRef.close({ action: 'view' });
                return [];
            }))
            .subscribe((pureBase64: string) => {
                const base64 = 'data:application/pdf;base64,' + pureBase64;

                this.viewDocument(base64);
                this.dialogRef.close({ action: 'view' });
            });
    }

    private viewDocument(base64: string) {
        this.disabled.set(true);
        this.showSpinner.set(true);

        const blob = base64toBlob(base64);
        const url = URL.createObjectURL(blob);
        const fileLike: Partial<FileMapEntry> = {
            type: 'application/pdf',
            localUrl: url,
            filename: this.generateFileName(),
        };

        const fullscreenDialogRef = this.dialog.open(FullScreenImageDialogComponent, {
            height: '95%',
            width: '95%',
            maxHeight: 'unset',
            maxWidth: 'unset',
            data: {
                file: fileLike,
            }
        });

        fullscreenDialogRef.afterClosed()
            .subscribe(() => {
                URL.revokeObjectURL(url);
            });
    }

    public async onPrint(): Promise<void> {
        this.disabled.set(true);
        this.showSpinner.set(true);

        if (await this.asyncConfirm('Download document?', 'By confirming, you will download the document. Thereafter, please use Adobe Acrobat and print it as you would any PDF document.')) {
            this.retrieveBase64ofDocument()
                .pipe(catchError((error) => {
                    console.error('Error downloading for printing document:', error);
                    this.snackbar.open('Error downloading for printing document. Please try again later.', 'Close', { duration: 5000 });
                    this.dialogRef.close({ action: 'print' });
                    return [];
                }))
                .subscribe((base64: string) => {
                    this.download(base64);
                    this.dialogRef.close({ action: 'print' });
                });
        }
    }

    public onDownload(): void {
        this.disabled.set(true);
        this.showSpinner.set(true);

        this.retrieveBase64ofDocument()
            .pipe(catchError((error) => {
                console.error('Error downloading document:', error);
                this.snackbar.open('Error downloading document. Please try again later.', 'Close', { duration: 5000 });
                this.dialogRef.close({ action: 'download' });
                return [];
            }))
            .subscribe((base64: string) => {
                this.download(base64);
                this.dialogRef.close({ action: 'download' });
            });

    }

    public onEmail(): void {
        this.disabled.set(true);
        this.showSpinner.set(true);

        this.emailDocument()
            .pipe(catchError((error) => {
                console.error('Error emailing document:', error);
                this.snackbar.open('Error emailing document. Please try again later.', 'Close', { duration: 5000 });
                this.dialogRef.close({ action: 'email' });
                return [];
            }))
            .subscribe(() => {
                this.snackbar.open('Email sent successfully.', 'Close', { duration: 5000 });
                this.dialogRef.close({ action: 'email' });
            });
    }

    private emailDocument(): Observable<void> {
        // emailVendor, 4, 5
        // emailVendorApplication, 1, 6
        // emailVendorApplicationCustomer, 2
        // customerProductEmail, 3
        switch (this.data.documentType) {
            case DocumentTypeEnum.StaffForm:
            case DocumentTypeEnum.PayoutReport:
                if (this.data.vendorApplication) return this.sharedApiService.emailVendor(this.data.documentType, this.data.vendorApplication.id);
                else throw new Error('Vendor application is missing');

            case DocumentTypeEnum.LoanAgreement:
            case DocumentTypeEnum.Quotation:
            case DocumentTypeEnum.SigningDocuments:
                if (this.data.vendorApplication) return this.sharedApiService.emailVendorApplication(this.data.documentType, this.data.vendorApplication.id);
                else throw new Error('Vendor application is missing');

            case DocumentTypeEnum.PatientAgreement:
                if (this.data.vendorApplication) return this.sharedApiService.emailVendorApplicationCustomer(this.data.documentType, this.data.vendorApplication.id);
                else throw new Error('Vendor application is missing');

            case DocumentTypeEnum.Statement:
                if (this.data.customerProduct) return this.sharedApiService.customerProductEmail(this.data.documentType, this.data.customerProduct.id);
                else throw new Error('Customer product is missing');

            default:
                console.error('Unknown document type:', this.data.documentType);
                throw new Error('Unknown document type');
        }
    }

    private retrieveBase64ofDocument(): Observable<string> {
        // vendorDocument, 4, 5
        // vendorApplicationDocument, 1,6,7
        // vendorApplicationCustomerDocument, 2
        // customerProductDocument, 3
        switch (this.data.documentType) {
            case DocumentTypeEnum.StaffForm:
            case DocumentTypeEnum.PayoutReport:
                if (this.data.vendorApplication) return this.sharedApiService.vendorDocument(this.data.documentType, this.data.vendorApplication.id);
                else throw new Error('Vendor application is missing');

            case DocumentTypeEnum.LoanAgreement:
            case DocumentTypeEnum.SigningDocuments:
            case DocumentTypeEnum.Quotation:
                if (this.data.vendorApplication) return this.sharedApiService.vendorApplicationDocument(this.data.documentType, this.data.vendorApplication.id);
                else if (this.data.documentType === DocumentTypeEnum.LoanAgreement && this.data.customerProduct) return this.sharedApiService.customerProductDocument(this.data.documentType, this.data.customerProduct.id);
                else throw new Error('Vendor application is missing');

            case DocumentTypeEnum.PatientAgreement:
                if (this.data.vendorApplication) return this.sharedApiService.vendorApplicationCustomerDocument(this.data.documentType, this.data.vendorApplication.vendorId);
                else throw new Error('Vendor application is missing');

            case DocumentTypeEnum.Statement:
                if (this.data.customerProduct) return this.sharedApiService.customerProductDocument(this.data.documentType, this.data.customerProduct.id);
                else throw new Error('Customer product is missing');

            default:
                console.error('Unknown document type:', this.data.documentType);
                throw new Error('Unknown document type');
        }
    }

    private download(base64: string) {
        const link = document.createElement('a');
        link.href = 'data:application/pdf;base64,' + base64;
        link.download = this.generateFileName();
        link.click();
    }

    private generateFileName(): string {
        const reference = this.data.customerProduct?.reference || this.data.vendorApplication?.reference;

        if (!reference) throw new Error('Reference is missing');

        switch (this.data.documentType) {
            case DocumentTypeEnum.StaffForm:
                return `Staff Form - ${reference}.pdf`;

            case DocumentTypeEnum.SigningDocuments:
                return `Quotation and Agreement - ${reference}.pdf`;

            case DocumentTypeEnum.PayoutReport:
                return `Payout Report - ${reference}.pdf`;

            case DocumentTypeEnum.LoanAgreement:
                return `Loan Agreement - ${reference}.pdf`;

            case DocumentTypeEnum.Quotation:
                return `Quotation - ${reference}.pdf`;

            case DocumentTypeEnum.PatientAgreement:
                return `Patient Agreement - ${reference}.pdf`;

            case DocumentTypeEnum.Statement:
                return `Statement - ${reference}.pdf`;

            default:
                console.error('Unknown document type:', this.data.documentType);
                throw new Error('Unknown document type');
        }
    }

    private asyncConfirm(title: string, description: string): Promise<boolean> {
        return firstValueFrom(this.dialog.open(ConfirmDialog, {
            data: {
                title: title,
                description: description,
                confirmResultData: true
            }
        }).afterClosed());
    }

}


interface Entity extends DataEntity {
    reference: string
}
